import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { noop } from 'ui-components/default-props/noop';

import QuickShipSettings from './quick-ship';
import commonStyles from '../settings-page.module.less';
import style from './style.module.less';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useIsQuickshipAllowed } from '../../../hooks/use-seller-setting';
import {
  useDefaultActionOptionSetting,
  useVerifyItemsBySetting,
  useRequiredManualConfirmationSetting,
  useQuickshipShowWarnings,
  useQuickshipShowErrors,
  useQuickshipShowCostSummary,
  useQuickshipShowPrintDialogPreview,
  useQuickshipShowLabelBatchStatus,
} from '../../../hooks/use-user-setting';
import { getUserSettingsOptions } from '../../../services/settings/user-settings-service';
import { updateUserSettingsBulk as updateUserSettingsBulkAction } from '../../../store/user/user.actions';
import { updateUserSettingsBulk } from '../api/save-settings';
import SaveBar from '../components/save-bar';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

const messages = defineMessages({
  header: {
    description: 'settings.workflow.header',
    defaultMessage: 'Workflow Settings',
    id: '4muTv1',
  },
  scanDescription: {
    description: 'settings.workflow.scanDescription',
    defaultMessage:
      "This option allows users to move from the 'Verify' step to the next step automatically (without needing to click 'Mark as Verified'). This change impacts the 'Scan to Verify' workflow by automatically moving to scan the next shipment and the 'Scan to Verify and Print' workflow by automatically moving to the print step.",
    id: 'fohKkc',
  },
});

const defaultActionOptions = getUserSettingsOptions(UserSettings.defaultButtonAction);
const verifyItemsByOptions = getUserSettingsOptions(UserSettings.verifyItemsBy);
const requiredManualConfirmationOptions = getUserSettingsOptions(UserSettings.requiredManualConfirmation);

export const Workflow = () => {
  const [defaultActionButton, setDefaultActionButton] = useState(useDefaultActionOptionSetting());
  const [verifyItemsBy, setVerifyItemsBy] = useState(useVerifyItemsBySetting());
  const [requiredManualConfirmation, setRequiredManualConfirmation] = useState(useRequiredManualConfirmationSetting());
  const [showErrors, setShowErrors] = useState(useQuickshipShowErrors());
  const [showWarnings, setShowWarnings] = useState(useQuickshipShowWarnings());
  const [showCostSummaryValue, setShowCostSummaryValue] = useState(useQuickshipShowCostSummary());
  const [showPrintDialogPreview, setShowPrintDialogPreview] = useState(useQuickshipShowPrintDialogPreview());
  const [showLabelBatchStatus, setShowLabelBatchStatus] = useState(useQuickshipShowLabelBatchStatus());

  const dispatch = useDispatch();

  const onChangeVerifyItemsBy = useCallback(
    (settingValue) => {
      const index = verifyItemsBy.indexOf(settingValue);
      if (index > -1) {
        setVerifyItemsBy([...verifyItemsBy.slice(0, index), ...verifyItemsBy.slice(index + 1)]);
        return;
      }
      setVerifyItemsBy([...verifyItemsBy, settingValue]);
    },
    [verifyItemsBy, setVerifyItemsBy],
  );

  const verifyItemsByContainsValue = useCallback((settingValue) => verifyItemsBy.includes(settingValue), [verifyItemsBy]);

  const onSave = useCallback(async () => {
    const settingsToSave = {
      [UserSettings.quickshipShowWarnings.settingName]: showWarnings,
      [UserSettings.quickshipShowErrors.settingName]: showErrors,
      [UserSettings.quickshipShowCostSummary.settingName]: showCostSummaryValue,
      [UserSettings.quickshipShowPrintDialogPreview.settingName]: showPrintDialogPreview,
      [UserSettings.quickshipShowLabelBatchStatus.settingName]: showLabelBatchStatus,
      [UserSettings.defaultButtonAction.settingName]: defaultActionButton,
      [UserSettings.verifyItemsBy.settingName]: verifyItemsBy,
      [UserSettings.requiredManualConfirmation.settingName]: requiredManualConfirmation,
    };

    const newSettings = await updateUserSettingsBulk(settingsToSave);
    dispatch(updateUserSettingsBulkAction(newSettings));
  }, [
    showErrors,
    showWarnings,
    showCostSummaryValue,
    showPrintDialogPreview,
    showLabelBatchStatus,
    defaultActionButton,
    verifyItemsBy,
    requiredManualConfirmation,
    dispatch,
  ]);

  return (
    <React.Fragment>
      <div className={commonStyles.content}>
        <div className={commonStyles['limited-width-page']}>
          <SettingsPageTitle value={messages.header} />
          <h3>
            <FormattedMessage {...UserSettings.defaultButtonAction.messageDescriptor} />
          </h3>
          <RadioCheckList className={style.section}>
            {defaultActionOptions.map((option) => (
              <RadioCheckListItem
                key={option.settingValue}
                className={style['section-item']}
                isChecked={defaultActionButton === option.settingValue}
                onChecked={() => setDefaultActionButton(option.settingValue)}
              >
                <FormattedMessage {...option.messageDescriptor} />
              </RadioCheckListItem>
            ))}
          </RadioCheckList>

          {useIsQuickshipAllowed() && (
            <QuickShipSettings
              setShowCostSummaryValue={setShowCostSummaryValue}
              setShowErrors={setShowErrors}
              setShowLabelBatchStatus={setShowLabelBatchStatus}
              setShowPrintDialogPreview={setShowPrintDialogPreview}
              setShowWarnings={setShowWarnings}
              showCostSummaryValue={showCostSummaryValue}
              showErrors={showErrors}
              showLabelBatchStatus={showLabelBatchStatus}
              showPrintDialogPreview={showPrintDialogPreview}
              showWarnings={showWarnings}
            />
          )}

          <h3>
            <FormattedMessage {...UserSettings.verifyItemsBy.messageDescriptor} />
          </h3>
          <div className={style.section}>
            {verifyItemsByOptions.map(({ settingValue, messageDescriptor }) => (
              <div
                key={settingValue}
                className={classnames(style['section-item'], style['checkbox-row'])}
                onClick={(e) => {
                  e.preventDefault();
                  onChangeVerifyItemsBy(settingValue);
                }}
              >
                <Checkbox
                  checked={verifyItemsByContainsValue(settingValue)}
                  label={<FormattedMessage {...messageDescriptor} />}
                  onChange={noop}
                  value={settingValue}
                />
              </div>
            ))}
          </div>

          <h3>
            <FormattedMessage {...UserSettings.requiredManualConfirmation.messageDescriptor} />
          </h3>
          <RadioCheckList className={style.section}>
            <div key={messages.scanDescription.description} className={classnames(style['section-item'], style['section-text'])}>
              <FormattedMessage {...messages.scanDescription} />
            </div>
            {requiredManualConfirmationOptions.map((option) => (
              <RadioCheckListItem
                key={option.settingValue}
                className={classnames(style['section-item'], style['radio-row'])}
                isChecked={requiredManualConfirmation === option.settingValue}
                onChecked={() => setRequiredManualConfirmation(option.settingValue)}
              >
                <FormattedMessage {...option.messageDescriptor} />
              </RadioCheckListItem>
            ))}
          </RadioCheckList>
        </div>
      </div>
      <SaveBar onSave={onSave} />
    </React.Fragment>
  );
};
